import { QUOTE_STATUS_PRIORITY } from "../constants";
import { ISOStringToUnixEpoch } from "./time";

// Sort by status priority, then newest first within a status.
export function sortQuotes<T extends CondensedQuote>(quotes: T[]): T[] {
  return [...quotes].sort((a, b) => {
    const priorityDifference =
      QUOTE_STATUS_PRIORITY[a.internal_state] -
      QUOTE_STATUS_PRIORITY[b.internal_state];
    if (priorityDifference !== 0) {
      return priorityDifference;
    }
    return (
      ISOStringToUnixEpoch(b.created_at) - ISOStringToUnixEpoch(a.created_at)
    );
  });
}

export function quoteCountText(quoteCount: number): string {
  if (quoteCount === 1) {
    return `${quoteCount} Quote`;
  }
  return `${quoteCount} Quotes`;
}

export function roleOwnsLatestQuote(quote: CondensedQuote, role: Role) {
  if (
    quote?.company != null &&
    quote?.company !== undefined &&
    role?.company?.id != null
  ) {
    return quote.company.id === role.company.id;
  }
  return false;
}

const getQuoteActionAuthorName = (
  quote: CondensedQuote,
  useLastNameInitial = false,
) => {
  const firstName =
    quote?.action_user_author?.first_name ??
    quote?.created_by?.first_name ??
    "-";
  const lastName =
    quote?.action_user_author?.last_name ?? quote?.created_by?.last_name ?? "-";
  const lastNameInitial =
    lastName && lastName.length > 0 ? `${lastName.charAt(0)}.` : "";
  if (useLastNameInitial) {
    return `${firstName} ${lastNameInitial}`;
  }
  return `${firstName} ${lastName}`;
};

const getQuoteActionCompany = (quote: CondensedQuote) => {
  const companyName =
    quote?.company?.name ?? quote?.created_by_company?.name ?? "-";
  return companyName;
};

const getSenderVerb = (quote: CondensedQuote) => {
  if (
    quote.internal_state === "ACTIVE" ||
    quote.internal_state === "EXPIRED" ||
    quote.internal_state === "SHIPMENT_CANCELLED" ||
    quote.internal_state === "SHIPMENT_EXPIRED" ||
    quote.internal_state === "SHIPMENT_LOST"
  ) {
    return "Quoted";
  }
  if (quote.internal_state === "COUNTERED") {
    return "Countered";
  }
  if (quote.internal_state === "CANCELLED") {
    return "Cancelled";
  }

  if (quote.internal_state === "ACCEPTED") {
    if (quote.assignment_type === "DIRECT_BOOKING") {
      return "Booked directly";
    }
    return "Booked";
  }

  return "";
};

const getLatestActionAuthor = (quote: CondensedQuote, role: Role | null) => {
  if (!role) {
    return "";
  }
  if (roleOwnsLatestQuote(quote, role) && role.type === "SHIPPER") {
    return `${getSenderVerb(quote)} by you to ${quote?.created_by?.first_name ?? "-"} ${quote?.created_by?.last_name ?? "-"} from ${quote?.created_by_company?.name ?? "-"}`;
  }

  if (roleOwnsLatestQuote(quote, role) && role.type === "CARRIER") {
    return `${getSenderVerb(quote)} by you`;
  }

  if (role.type === "SHIPPER") {
    return `${getSenderVerb(quote)} by ${getQuoteActionAuthorName(quote)} from ${getQuoteActionCompany(quote)}`;
  }
  if (role.type === "CARRIER") {
    // carriers do not get to see full name and company info,
    // only first name and initial of last name
    return `${getSenderVerb(quote)} by ${getQuoteActionAuthorName(quote, true)}`;
  }
  return "";
};

export const getQuoteSenderLabel = (
  quote: CondensedQuote,
  role: Role | null,
) => {
  if (
    role &&
    (quote.internal_state === "ACTIVE" ||
      quote.internal_state === "COUNTERED" ||
      quote.internal_state === "EXPIRED" ||
      quote.internal_state === "CANCELLED" ||
      quote.internal_state === "SHIPMENT_LOST" ||
      quote.internal_state === "SHIPMENT_CANCELLED" ||
      quote.internal_state === "SHIPMENT_EXPIRED" ||
      quote.internal_state === "ACCEPTED")
  ) {
    return getLatestActionAuthor(quote, role);
  }

  return "";
};

export const getNoteLabel = (role: Role | null) => {
  if (role == null) {
    return "";
  }
  return "Notes: ";
};

// Shipment statuses that represent a terminal outcome once a quote is booked.
const TERMINAL_SHIPMENT_STATUSES: ShipmentStatusValue[] = [
  "BOOKING_CANCELLED",
  "DELIVERED",
  "PICKED_UP",
];

// True once a quote is accepted and its shipment has reached a terminal state,
// at which point the quote-state badge is replaced by the shipment-outcome chip.
export function isAcceptedTerminalQuote(quote: CondensedQuote): boolean {
  return (
    quote.internal_state === "ACCEPTED" &&
    quote.shipment_status != null &&
    TERMINAL_SHIPMENT_STATUSES.includes(quote.shipment_status)
  );
}

// docs/engineering/design/quote_competitiveness_scoring_design.md §14/§15 — which status
// icon a quote's competitiveness score carries. ACCEPTED (this carrier won)
// is "check"; ACTIVE/COUNTERED (still bookable) is "circle"; every other
// terminal status (declined/cancelled/expired/lost) is "lock" — the quote
// became unbookable some other way.
export function getQuoteScoreStatusIcon(
  internalState: InternalQuoteStatusValue,
): Exclude<QuoteScoreStatusIcon, "none"> {
  if (internalState === "ACCEPTED") return "check";
  if (internalState === "ACTIVE" || internalState === "COUNTERED")
    return "circle";
  return "lock";
}

// A "dead" quote (declined/cancelled/expired/lost) fades its own fields and
// mutes its competitiveness verdict, since it can no longer be acted on.
// ACCEPTED is not dead — a carrier who won stays in full color.
export function isDeadQuote(internalState: InternalQuoteStatusValue): boolean {
  return getQuoteScoreStatusIcon(internalState) === "lock";
}

const QUOTE_SCORE_LEVEL_RANK: Record<QuoteScoreLevel, number> = {
  very_competitive: 3,
  competitive: 2,
  somewhat_competitive: 1,
  not_competitive: 0,
};

export type CompetitivenessSort = "MOST_COMPETITIVE" | "LEAST_COMPETITIVE";

export function isCompetitivenessSort(
  sort: QuoteSortType,
): sort is CompetitivenessSort {
  return sort === "MOST_COMPETITIVE" || sort === "LEAST_COMPETITIVE";
}

// Whether any quote has a verdict to sort by. A score row alone is not enough:
// the only quote on a load is scored but has no overall label.
export function hasCompetitivenessVerdict(
  scoreByQuoteId: Map<number, ShipperQuoteScore>,
): boolean {
  return [...scoreByQuoteId.values()].some(
    (quoteScore) => quoteScore.overall_label != null,
  );
}

// Quotes without a verdict (not scored yet, or the only quote on the load) go
// last in both directions: they are neither the most nor the least competitive.
export function sortQuotesByCompetitiveness(
  quotes: ShipperCondensedQuote[],
  scoreByQuoteId: Map<number, ShipperQuoteScore>,
  direction: CompetitivenessSort,
): ShipperCondensedQuote[] {
  const getRank = (quote: ShipperCondensedQuote): number | null => {
    const label = scoreByQuoteId.get(quote.quote)?.overall_label;
    return label != null ? QUOTE_SCORE_LEVEL_RANK[label] : null;
  };
  return [...quotes].sort((a, b) => {
    const aRank = getRank(a);
    const bRank = getRank(b);
    if (aRank === null || bRank === null) {
      return Number(aRank === null) - Number(bRank === null);
    }
    return direction === "MOST_COMPETITIVE" ? bRank - aRank : aRank - bRank;
  });
}

// Which of the two reasons (if either) blocks a carrier from submitting a new
// quote on a shipment: an existing live quote of their own takes precedence
// over the shipment having resolved without them, since cancelling that quote
// is the more specific fix.
export function getSubmitQuoteBlockReason(
  hasActiveQuote: boolean,
  shipmentBookedElsewhere: boolean,
): SubmitQuoteBlockReason | null {
  if (hasActiveQuote) return "ACTIVE_QUOTE";
  if (shipmentBookedElsewhere) return "SHIPMENT_BOOKED_ELSEWHERE";
  return null;
}

// The message shown in place of the submit-quote form when
// getSubmitQuoteBlockReason() returns non-null.
export function getSubmitQuoteBlockMessage(
  reason: SubmitQuoteBlockReason,
): string {
  if (reason === "SHIPMENT_BOOKED_ELSEWHERE") {
    return "This shipment has been booked with another carrier.";
  }
  return "You already have an active quote on this shipment. To submit a new quote, cancel the existing quote.";
}

export const getBookedQuoteFromQuotes = (quotes: CondensedQuote[]) =>
  quotes.find((q: CondensedQuote) => q.internal_state === "ACCEPTED");

export const getBookedQuoteForShipperCondensedShipment = (
  shipment: ShipperCondensedShipment,
) => {
  const booked = getBookedQuoteFromQuotes(shipment.condensed_quotes);
  return booked;
};

export function getActiveQuoteFiltersCount(
  filters: QuoteFilters | null,
): number {
  if (!filters) return 0;

  let filterCount = 0;

  if (filters.shipmentName && filters.shipmentName.trim() !== "")
    filterCount += 1;
  if (filters.quoteStatus && filters.quoteStatus.length > 0) filterCount += 1;
  if (filters.equipmentTypes && filters.equipmentTypes.length > 0)
    filterCount += 1;
  if (filters.serviceTypes && filters.serviceTypes.length > 0) filterCount += 1;
  const hasActiveShipmentTypeFilter =
    filters.shipmentTypes &&
    filters.shipmentTypes.length > 0 &&
    !(filters.shipmentTypes.length === 1 && filters.shipmentTypes[0] === "ALL");
  if (hasActiveShipmentTypeFilter) filterCount += 1;
  if (filters.originProvinces && filters.originProvinces.length > 0)
    filterCount += 1;
  if (filters.destinationProvinces && filters.destinationProvinces.length > 0)
    filterCount += 1;
  if (filters.pickupDateAfter || filters.pickupDateBefore) filterCount += 1;
  if (filters.deliveryDateAfter || filters.deliveryDateBefore) filterCount += 1;
  if (filters.shipmentStatus && filters.shipmentStatus.length > 0)
    filterCount += 1;

  return filterCount;
}

export const shipperFilterQuotes = (
  quotes: ShipperCondensedQuote[],
  filter: QuoteFilterType,
) => {
  if (filter === "ALL") {
    return quotes;
  }
  if (filter === "PARTNER") {
    return quotes.filter((quote) => quote.is_partner_quote);
  }
  if (filter === "ACTIVE") {
    return quotes.filter(
      (quote) =>
        quote.internal_state === "ACTIVE" ||
        quote.internal_state === "COUNTERED",
    );
  }
  if (
    filter === "CANCELLED" ||
    filter === "COUNTERED" ||
    filter === "ACCEPTED"
  ) {
    return quotes.filter((quote) => quote.internal_state === filter);
  }
  if (filter === "LOST") {
    return quotes.filter((quote) => quote.internal_state === "SHIPMENT_LOST");
  }

  if (filter === "BY_TRUCKING_COMPANY") {
    return quotes.filter(
      (quote) => quote.created_by_company.business_type === "TRUCKING_COMPANY",
    );
  }

  if (filter === "BY_BROKER") {
    return quotes.filter(
      (quote) => quote.created_by_company.business_type === "BROKER",
    );
  }

  return quotes;
};

export const equipmentTypeInEquipmentList = (
  equipmentType: string | undefined,
  equipments: Equipment[] | undefined,
): boolean => {
  if (!equipmentType || !equipments) {
    return false;
  }
  return equipments.some((equipment) => equipment.type === equipmentType);
};

export function formatQuotingUnitOfMeasure(
  quotingUnitOfMeasure: UnitOfMeasureType,
  isTitle = true,
): string {
  if (isTitle) {
    if (quotingUnitOfMeasure === "ALL_IN") {
      return "All-in Price";
    }

    const humanReadable = quotingUnitOfMeasure
      .toLowerCase()
      .split("_")
      .join(" ");
    return `Price ${humanReadable}`;
  }
  return quotingUnitOfMeasure.toLowerCase().split("_").join(" ");
}

export function getUnitOfMeasureTitle(
  quotingUnitOfMeasure: UnitOfMeasureType,
): string {
  if (quotingUnitOfMeasure === "PER_METRIC_TON") {
    return "metric ton";
  }
  if (quotingUnitOfMeasure === "PER_SHORT_TON") {
    return "short ton";
  }
  if (quotingUnitOfMeasure === "PER_HOUR") {
    return "hour";
  }
  return quotingUnitOfMeasure.toLowerCase().split("_").join(" ");
}

export function getUnitOfMeasureLabel(
  quotingUnitOfMeasure: UnitOfMeasureType,
): string {
  const formatted = formatQuotingUnitOfMeasure(quotingUnitOfMeasure, false);
  return formatted.replace("per ", "").concat("s");
}

// Appends the quoting unit ("per hour") unless the price is all-in.
function appendQuotingUnit(
  formattedPrice: string,
  quotingUnitOfMeasure?: UnitOfMeasureType,
): string {
  if (quotingUnitOfMeasure && quotingUnitOfMeasure !== "ALL_IN") {
    return `${formattedPrice} ${formatQuotingUnitOfMeasure(quotingUnitOfMeasure, false)}`;
  }
  return formattedPrice;
}

export function formatRateWithQuotingUnit(
  price: string | number,
  currency: string,
  quotingUnitOfMeasure: UnitOfMeasureType,
): string {
  return appendQuotingUnit(`$${price} ${currency}`, quotingUnitOfMeasure);
}

export const getQuoteTitle = (quote: CondensedQuote) => {
  if (
    quote.internal_state === "ACTIVE" ||
    quote.internal_state === "COUNTERED" ||
    quote.internal_state === "CANCELLED" ||
    quote.internal_state === "EXPIRED" ||
    quote.internal_state === "SHIPMENT_CANCELLED" ||
    quote.internal_state === "SHIPMENT_EXPIRED" ||
    quote.internal_state === "ACCEPTED" ||
    quote.internal_state === "SHIPMENT_LOST" ||
    quote.internal_state === "DECLINED"
  ) {
    return formatRateWithQuotingUnit(
      quote?.price ?? "-",
      quote?.currency ?? "-",
      quote.quoting_unit_of_measure,
    );
  }
  return "";
};

/*
Every amount this module renders is money, so it gets exactly two decimals.
Bare toLocaleString gave neither bound: it dropped a trailing zero, so a cargo
coverage of 250000.50 read "250,000.5", and it would have printed a third
decimal had one ever reached the client.
*/
const MONEY_FRACTION_DIGITS: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

function toMoneyString(amount: number): string {
  return amount.toLocaleString(undefined, MONEY_FRACTION_DIGITS);
}

export function formatPrice(
  price: string,
  currency: string,
  quotingUnitOfMeasure?: UnitOfMeasureType,
): string {
  if (!price || price === "") return "Not specified";
  const numPrice = parseFloat(price);
  if (Number.isNaN(numPrice)) return price;

  if (!currency || currency === "") {
    return `${toMoneyString(numPrice)} (Currency not specified)`;
  }

  let currencySymbol = "";
  if (currency === "USD") {
    currencySymbol = "USD";
  } else if (currency === "CAD") {
    currencySymbol = "CAD";
  } else {
    currencySymbol = currency;
  }

  return appendQuotingUnit(
    `${toMoneyString(numPrice)} ${currencySymbol}`,
    quotingUnitOfMeasure,
  );
}

/**
 * Currency normalization for price sorting
 * Converts USD to CAD using 1.4x multiplier for consistent comparison
 */
function normalizePriceToCAD(price: string, currency: string): number {
  const numPrice = parseFloat(price);

  if (Number.isNaN(numPrice)) {
    return 0;
  }

  if (currency === "USD") {
    return numPrice * 1.4; // Convert USD to CAD
  }

  return numPrice; // Assume CAD or return as-is
}

/**
 * Get normalized price for sorting while preserving original display value
 */
export function getQuoteNormalizedPrice(quote: ShipperCondensedQuote): number {
  if (!quote.price || !quote.currency) {
    return 0;
  }

  return normalizePriceToCAD(quote.price.toString(), quote.currency);
}
